import * as api from '@api';
import useToast from '@hooks/useToast';
import type { UploadedFileData } from '@retracedgmbh/ui-components-lib';
import { generateId } from '@utils';
import { useCallback, useRef, useState } from 'react';
import SparkMD5 from 'spark-md5';
import Uploader from '../lib/uploader';
import {
  EXTENSION_TO_MIME,
  normalizeAcceptedFileTypes,
} from './file-upload-constants';

// ─── Types ──────────────────────────────────────────────────────────────────

/** File data shape used during and after upload */
export interface UploadFileData {
  id?: string;
  url: string | null;
  contentType?: string;
  fileType?: string;
  bucketName?: string;
  progress: number;
  uploadFileId: string;
  fileXhrRequest: XMLHttpRequest | null;
  md5?: string;
  [key: string]: unknown;
}

interface StartedFile {
  file: File;
  uploadFileId: string;
  res?: Record<string, unknown>;
}

export interface UploadingFile {
  uploadFileId: string;
  name: string;
  progress: number;
  fileXhrRequest: XMLHttpRequest | null;
}

export interface UseFileUploadNewOptions {
  isPrivate?: boolean;
  isMultiple?: boolean;
  acceptedFileTypes?: string[] | Record<string, string[]>;
  initialPreview?: UploadFileData[];
  onUploadFinish?: (files: UploadFileData[]) => void;
  onUploadProgress?: (files: UploadFileData[]) => void;
  onUploadStart?: (files: StartedFile[]) => void;
  onSessionDuplicates?: (fileNames: string[]) => void;
  checkForDatabaseDuplicates?: (md5Hashes: string[]) => Promise<string[]>;
  onDatabaseDuplicates?: (fileNames: string[]) => void;
}

export interface UseFileUploadNewReturn {
  displayFiles: UploadedFileData[];
  uploadingFiles: UploadingFile[];
  mimeAccept: Record<string, string[]>;
  handleDrop: (acceptedFiles: File[]) => void;
  handleDelete: (file: UploadedFileData) => void;
  handleView: (file: UploadedFileData) => void;
  handleDownload: (file: UploadedFileData) => void;
  handleDownloadAll: () => void;
}

// ─── MD5 helper ─────────────────────────────────────────────────────────────

// Some browsers report an empty `file.type` for less common formats (e.g. .avi,
// .mkv). Fall back to the extension→MIME map so the presigned-URL request still
// sends a content type the backend allow-list recognises.
function resolveFileType(file: File): string {
  if (file.type) return file.type;
  const idx = file.name.lastIndexOf('.');
  const ext = idx !== -1 ? file.name.slice(idx).toLowerCase() : '';
  return EXTENSION_TO_MIME[ext] ?? '';
}

async function computeMd5(file: File): Promise<string> {
  const hex = SparkMD5.ArrayBuffer.hash(await file.arrayBuffer());
  const bytes = new Uint8Array(
    hex.match(/.{2}/g)!.map(b => Number.parseInt(b, 16)),
  );
  return btoa(String.fromCharCode(...bytes));
}

// ─── UploadFileData → UploadedFileData mapper ───────────────────────────────

function toUploadedFileData(file: UploadFileData): UploadedFileData {
  return {
    id: file.id ?? file.uploadFileId,
    filename: (file.name as string) ?? (file.filename as string) ?? 'File',
    url: file.url ?? '',
    contentType: file.contentType,
    fileType: file.fileType,
    size: file.size as number | undefined,
  };
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useFileUploadNew(
  options: UseFileUploadNewOptions = {},
): UseFileUploadNewReturn {
  const {
    isMultiple = false,
    acceptedFileTypes,
    initialPreview = [],
    onUploadFinish = () => {},
    onUploadProgress = () => {},
    onUploadStart = () => {},
    onSessionDuplicates,
    checkForDatabaseDuplicates,
    onDatabaseDuplicates,
  } = options;

  const { toast } = useToast();

  const [files, setFiles] = useState<UploadFileData[]>(initialPreview);
  const [uploadingFiles, setUploadingFiles] = useState<UploadFileData[]>([]);

  const filesRef = useRef(files);
  filesRef.current = files;
  const uploadingFilesRef = useRef(uploadingFiles);
  uploadingFilesRef.current = uploadingFiles;

  const mimeAccept = normalizeAcceptedFileTypes(acceptedFileTypes);

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      type FileWithId = {
        file: File;
        uploadFileId: string;
        error?: true;
        res?: Record<string, unknown>;
        md5?: string;
      };

      // Phase 1: compute MD5 for all dropped files in parallel
      let filesWithHashes: FileWithId[] = await Promise.all(
        acceptedFiles.map(async f => ({
          file: f,
          uploadFileId: generateId(),
          md5: await computeMd5(f).catch(() => undefined),
        })),
      );

      // Phase 2 (opt-in): discard files whose hash is already in this session
      if (onSessionDuplicates) {
        const existingHashes = new Set(
          filesRef.current
            .map(f => f.md5)
            .filter((h): h is string => Boolean(h)),
        );
        const duplicates = filesWithHashes.filter(
          af => af.md5 && existingHashes.has(af.md5),
        );
        if (duplicates.length > 0) {
          onSessionDuplicates(duplicates.map(af => af.file.name));
        }
        filesWithHashes = filesWithHashes.filter(
          af => !af.md5 || !existingHashes.has(af.md5),
        );
      }

      // Phase 2b (opt-in): discard files already present in the database
      if (checkForDatabaseDuplicates) {
        const hashesToCheck = filesWithHashes
          .map(f => f.md5)
          .filter((h): h is string => Boolean(h));
        if (hashesToCheck.length > 0) {
          const existingDbHashes = new Set(
            await checkForDatabaseDuplicates(hashesToCheck),
          );
          const dbDuplicates = filesWithHashes.filter(
            af => af.md5 && existingDbHashes.has(af.md5),
          );
          if (dbDuplicates.length > 0) {
            onDatabaseDuplicates?.(dbDuplicates.map(af => af.file.name));
          }
          filesWithHashes = filesWithHashes.filter(
            af => !af.md5 || !existingDbHashes.has(af.md5),
          );
        }
      }

      if (filesWithHashes.length === 0) return;

      // Phase 3: fetch presigned URLs for remaining files in parallel
      const acceptedFilesWithId: FileWithId[] = await Promise.all(
        filesWithHashes.map(async af => {
          const res = await api.backend.uploadURLPost({
            filename: af.file.name,
            fileType: resolveFileType(af.file),
            fileSize: af.file.size,
          });
          if (res.error) {
            toast.error(`Failed to upload ${af.file.name}`);
            return { ...af, error: true as const };
          }
          return {
            ...af,
            res: res.data?.data as Record<string, unknown> | undefined,
          };
        }),
      );

      const processedFiles: UploadFileData[] = [];
      const processingFiles: UploadFileData[] = [];

      for (const af of acceptedFilesWithId) {
        if (af.error) continue;

        const fileResponseData = af.res as Record<string, unknown> & {
          id: string;
          url: string;
        };

        const uploader = new Uploader({
          file: af.file,
          url: fileResponseData.url,
          onComplete: () => {
            const completedFile: UploadFileData = {
              ...fileResponseData,
              url: fileResponseData.url,
              progress: 100,
              uploadFileId: af.uploadFileId,
              fileXhrRequest: null,
              name: af.file.name,
              size: af.file.size,
              md5: af.md5,
            };

            processedFiles.push(completedFile);

            if (
              processedFiles.length ===
              acceptedFilesWithId.filter(f => !('error' in f && f.error)).length
            ) {
              setTimeout(() => {
                const merged = isMultiple
                  ? [...filesRef.current, ...processedFiles]
                  : [...processedFiles];
                setFiles(merged);
                setUploadingFiles([]);
                if (merged.length > 0) {
                  onUploadFinish(merged);
                }
              }, 1000);
            }
          },
          onError: () => {
            toast.error(`Upload failed for ${af.file.name}`);
            const idx = processingFiles.findIndex(
              x => x.id === fileResponseData.id,
            );
            if (idx >= 0) {
              processingFiles.splice(idx, 1);
              setUploadingFiles([...processingFiles]);
            }
          },
          onProgress: (e: ProgressEvent) => {
            const idx = processingFiles.findIndex(
              x => x.id === fileResponseData.id,
            );
            if (idx >= 0) {
              processingFiles[idx].progress = Math.round(
                (e.loaded / e.total) * 100,
              );
              processingFiles[idx].url = null;
            }
            const updatedUploading = [...processingFiles];
            setUploadingFiles(updatedUploading);
            const allFiles = isMultiple
              ? [...filesRef.current, ...updatedUploading]
              : [...updatedUploading];
            onUploadProgress(allFiles);
          },
          onAbort: () => {
            const idx = processingFiles.findIndex(
              x => x.id === fileResponseData.id,
            );
            if (idx >= 0) {
              processingFiles.splice(idx, 1);
              setUploadingFiles([...processingFiles]);
            }
          },
        });

        processingFiles.push({
          ...fileResponseData,
          url: null,
          progress: 0,
          uploadFileId: af.uploadFileId,
          fileXhrRequest: uploader.fileXhrRequest,
          name: af.file.name,
          size: af.file.size,
        });

        uploader.sendFile();
      }

      setUploadingFiles([...processingFiles]);
      onUploadStart(
        acceptedFilesWithId.filter(
          f => !('error' in f && f.error),
        ) as StartedFile[],
      );
    },
    [
      isMultiple,
      onUploadFinish,
      onUploadProgress,
      onUploadStart,
      onSessionDuplicates,
    ],
  );

  const handleDelete = useCallback(
    (file: UploadedFileData) => {
      const updated = filesRef.current.filter(
        f => (f.id ?? f.uploadFileId) !== file.id,
      );
      setFiles(updated);
      onUploadFinish(updated);
    },
    [onUploadFinish],
  );

  const handleView = useCallback((file: UploadedFileData) => {
    if (file.url) {
      window.open(file.url, '_blank', 'noopener,noreferrer');
    }
  }, []);

  const handleDownload = useCallback((file: UploadedFileData) => {
    if (file.url) {
      const a = document.createElement('a');
      a.href = file.url;
      a.download = file.filename || 'download';
      a.click();
    }
  }, []);

  const displayFiles: UploadedFileData[] = files
    .filter(f => f.progress === 100 && f.url)
    .map(toUploadedFileData);

  const handleDownloadAll = useCallback(() => {
    for (const file of displayFiles) {
      if (file.url) {
        const a = document.createElement('a');
        a.href = file.url;
        a.download = file.filename || 'download';
        a.click();
      }
    }
  }, [displayFiles]);

  const mappedUploadingFiles: UploadingFile[] = uploadingFiles.map(uf => ({
    uploadFileId: uf.uploadFileId,
    name: (uf.name as string) ?? 'Uploading...',
    progress: uf.progress,
    fileXhrRequest: uf.fileXhrRequest,
  }));

  return {
    displayFiles,
    uploadingFiles: mappedUploadingFiles,
    mimeAccept,
    handleDrop,
    handleDelete,
    handleView,
    handleDownload,
    handleDownloadAll,
  };
}
